import { datadogRum } from '@datadog/browser-rum'
import { PromotionTarget, StorefrontItemsVisibility, type Promotion } from '@rediredi/types'
import { IStorefrontItem } from '~/types/item'
import { Facet } from '~/types/api'

export const useItemsStore = defineStore('items', {
  state: () => ({
    query: ref('') as Ref<string | undefined>,
    term: ref('') as Ref<string | undefined>,
    promoId: ref('') as Ref<string | undefined>,
    facetBy: ref<Facet | null>(null),
    perPage: 20,
    sortBy: null as string | null,
    order: null as 'asc' | 'desc' | null,
    totalRecords: undefined as number | undefined,
    currentPage: 1,
    scrollPosition: 0,
    items: [] as IStorefrontItem[],
    facets: [] as Facet[],
    hasInitiallyLoaded: false,
    hasPromotions: false,
    filterByPromotion: false,
    currentPromotion: null as Promotion | null,
    spotlight: [] as IStorefrontItem[],
  }),

  getters: {
    sortedFacets: (state) => {
      return state.facets.sort((a, b) => a.value.localeCompare(b.value))
    },

    sortedParents: (state) => {
      const parents: Facet[] = []
      const chields: Facet[] = state.facets.filter((facet) => !!facet.parent)

      state.facets.forEach((facet) => {
        if (parents.find((parent) => parent.id === facet.parent?.id || parent.id === facet.id))
          return
        parents.push({
          ...(facet.parent || facet),
          hasChildren:
            !!chields.find(({ parent }) => (parent ? parent.id === facet.id : false)) ||
            !!facet.parent,
        })
      })

      return parents.sort((a, b) => a.value.localeCompare(b.value))
    },

    childrenFacets: (state) => {
      return (parentId: string) => {
        return state.facets
          .filter((facet) => facet.parent?.id === parentId)
          .sort((a, b) => a.value.localeCompare(b.value))
      }
    },

    facetsValueTree: (state) => {
      if (!state.facetBy) return []
      const tree = []

      if (state.facetBy.parent) tree.push(state.facetBy.parent?.value)
      tree.push(state.facetBy.value)

      return tree
    },

    filterBy: (state) => {
      const globalStore = useGlobalStore()

      const filters = []

      if (state.filterByPromotion) {
        filters.push(`hasActivePromotion:=true`)
      }

      if (state.currentPromotion) {
        filters.push(`promotionId:=${state.currentPromotion.id}`)
      }

      if (!globalStore.store.displayOutOfStockItems) {
        filters.push('quantityAvailable:>0')
      }

      if (
        globalStore.store.salesperson &&
        globalStore.store.salesperson.storefrontPreferences.itemsVisibility ===
          StorefrontItemsVisibility.SpecificItems
      ) {
        filters.push(`salespeopleIds:=[${globalStore.store.salesperson.id}]`)
      }

      return filters.join('&&')
    },
  },

  actions: {
    clearOnSearch() {
      this.currentPage = 1
      this.scrollPosition = 0
      this.items = []
      this.term = undefined
      this.query = undefined
      this.filterByPromotion = false
      this.currentPromotion = null
      this.promoId = undefined
    },

    async searchItems(override = false) {
      const sortBy = this.sortBy ? `${this.sortBy}.${this.order}` : 'title.asc'
      try {
        const params = {
          sortBy,
          perPage: this.perPage.toString(),
          page: this.currentPage.toString(),
          query: this.query || this.facetBy?.id || '',
          filterBy: this.filterBy,
        }

        const response = await useFetchWrapper<SearchResponse<IStorefrontItem>>(
          `/inventory/storefront/items?${new URLSearchParams(params)}`,
          'GET',
        )

        if (override) {
          this.items = response.data
        }

        return response
      } catch (error) {
        datadogRum.addError(error)
      }
    },

    async getSpotlightItems() {
      try {
        const globalStore = useGlobalStore()
        const filters = ['spotlightPreferencesEnabled:=true']

        if (!globalStore.store.displayOutOfStockItems) {
          filters.push('quantityAvailable:>0')
        }

        const params = {
          filterBy: filters.join('&&'),
        }

        const response = await useFetchWrapper<SearchResponse<IStorefrontItem>>(
          `/inventory/storefront/items?${new URLSearchParams(params)}`,
          'GET',
        )

        return response
      } catch (error) {
        datadogRum.addError(error)
      }
    },

    async loadPromotion() {
      const response = await useFetchWrapper<Promotion>(
        `/promotions/storefront/promotions/${this.promoId}`,
        'GET',
      )
      this.currentPromotion = response || null

      if (response.target === PromotionTarget.Category) {
        const category = response.category

        if (category?.parent) {
          this.term = `${category?.parent.name}_${category?.name}`
        } else {
          this.term = category?.name
        }
      }
      return response
    },

    async loadFacetsV2() {
      const qs: Record<string, string> = {
        facetBy: 'promotion',
        perPage: '0',
      }

      const globalStore = useGlobalStore()

      if (
        globalStore.store.salesperson &&
        globalStore.store.salesperson.storefrontPreferences.itemsVisibility ===
          StorefrontItemsVisibility.SpecificItems
      ) {
        qs.filterBy = `salespeopleIds:=[${globalStore.store.salesperson.id}]`
      }

      const response = await useFetchWrapper<SearchResponse<IStorefrontItem>>(
        `/inventory/storefront/v2/items?${new URLSearchParams(qs)}`,
        'GET',
      )

      this.facets = response.meta.facets?.category || []
      this.hasPromotions = !!response.meta.facets?.promotion?.length

      this.totalRecords = response.meta.total

      return response
    },

    async loadFacets() {
      const response = await useFetchWrapper<SearchResponse<IStorefrontItem>>(
        `/inventory/storefront/items`,
        'GET',
      )

      this.facets = response.meta.facets

      this.totalRecords = response.meta.total

      return response
    },

    setSort(sort: { attribute: string | null; direction: 'asc' | 'desc' | null }) {
      this.currentPage = 1
      this.sortBy = sort.attribute
      this.order = sort.direction
      this.searchItems(true)
    },
  },
})
